import React from 'react';
import { Token } from '../../types/tournament';

interface TokenAvatarProps {
  token: Token;
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  showLiquidity?: boolean;
  showFullName?: boolean;
  isWinner?: boolean;
  isLoser?: boolean;
  useOriginalImage?: boolean;
  className?: string;
  responsiveSize?: boolean; // Enable responsive sizing
}

const sizeClasses = {
  xs: 'w-4 h-4 text-[9px]',
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-10 h-10 text-xs',
  lg: 'w-16 h-16 text-sm',
  xl: 'w-24 h-24 text-xl',
  xxl: 'w-48 h-48 text-3xl',
};

// Responsive size classes that adapt to screen size
const responsiveSizeClasses = {
  xs: 'w-4 h-4 text-[9px]',
  sm: 'w-5 h-5 sm:w-6 sm:h-6 text-[9px] sm:text-[10px]',
  md: 'w-8 h-8 sm:w-10 sm:h-10 text-[10px] sm:text-xs',
  lg: 'w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 text-xs sm:text-sm',
  xl: 'w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-sm sm:text-lg lg:text-xl',
  xxl: 'w-24 h-24 sm:w-32 sm:h-32 lg:w-48 lg:h-48 text-lg sm:text-2xl lg:text-3xl',
};

// Container widths in rem units (tailwind sizing)
const containerWidths = {
  xs: 1,    // w-4 = 1rem = 16px
  sm: 1.5,  // w-6 = 1.5rem = 24px
  md: 2.5,  // w-10 = 2.5rem = 40px
  lg: 4,    // w-16 = 4rem = 64px
  xl: 6,    // w-24 = 6rem = 96px
  xxl: 12,  // w-48 = 12rem = 192px
};

const borderClasses = {
  xs: '',
  sm: '',
  md: 'shadow-md',
  lg: 'shadow-lg border border-white/10',
  xl: 'shadow-2xl border-2 border-white/20',
  xxl: 'shadow-2xl border-2 border-white/30',
};

export const TokenAvatar: React.FC<TokenAvatarProps> = ({
  token,
  size,
  showLiquidity = false,
  showFullName = false,
  isWinner = false,
  isLoser = false,
  useOriginalImage = false,
  className = '',
  responsiveSize = false,
}) => {
  const displayName = showFullName 
    ? token.symbol 
    : size === 'xs' 
      ? token.symbol[0]
      : size === 'sm' 
        ? token.symbol.slice(0, 2)
        : size === 'md'
          ? token.symbol.slice(0, 3)
          : token.symbol;

  // Calculate font size to fit text in container
  const getFontSizeClass = () => {
    const nameLength = displayName.length;
    const containerWidth = containerWidths[size];
    
    // Approximate: each character needs about 0.6em width for bold text
    // Container width in rem, we need to fit the text with some padding
    const availableWidth = containerWidth * 0.9; // 90% of container width for padding
    const neededWidth = nameLength * 0.6; // approximate em units needed
    
    // Calculate scale factor
    const scaleFactor = availableWidth / neededWidth;
    
    // Base font sizes in px
    const baseFontSizes = {
      xs: 9,
      sm: 10,
      md: 12,
      lg: 14,
      xl: 20,
      xxl: 28,
    };
    
    const baseFontSize = baseFontSizes[size];
    let targetFontSize = baseFontSize;
    
    // Only scale down if needed
    if (scaleFactor < 1) {
      targetFontSize = Math.max(6, Math.floor(baseFontSize * scaleFactor));
    }
    
    // Return Tailwind class or custom size
    if (targetFontSize >= 20) return 'text-xl';
    if (targetFontSize >= 18) return 'text-lg';
    if (targetFontSize >= 16) return 'text-base';
    if (targetFontSize >= 14) return 'text-sm';
    if (targetFontSize >= 12) return 'text-xs';
    return `text-[${targetFontSize}px]`;
  };

  const baseSizeClass = responsiveSize ? responsiveSizeClasses[size] : sizeClasses[size];
  const customFontSize = getFontSizeClass();
  // Override the font size from sizeClasses if we have a custom size
  const sizeClass = customFontSize 
    ? baseSizeClass.split(' ').filter(c => !c.startsWith('text-')).join(' ') + ' ' + customFontSize
    : baseSizeClass;

  const roundedClass = size === 'xs' || size === 'sm' ? 'rounded-sm' : size === 'md' ? 'rounded' : 'rounded-lg';
  
  // Apply winner/loser styles - removed opacity reduction for bracket screen
  const statusClasses = '';

  // Generate a color from the token symbol if no color is provided
  const generateColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  const tokenColor = generateColor(token.symbol);

  return (
    <div className={`flex flex-col items-center space-y-1 ${className}`}>
      {token.imagePath ? (
        <div
          className={`${sizeClass} ${borderClasses[size]} ${roundedClass} ${statusClasses} overflow-hidden transition-all`}
          title={token.name}
        >
          <img 
            src={useOriginalImage 
              ? `/tokens/images/${token.imagePath}`
              : `/tokens/images/${token.imagePath.replace('.png', '.jpg')}`
            }
            alt={token.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to color avatar if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <div 
            className="w-full h-full items-center justify-center hidden"
            style={{ backgroundColor: tokenColor }}
          >
            <span className="text-white font-bold">
              {displayName}
            </span>
          </div>
        </div>
      ) : (
        <div
          className={`${sizeClass} ${borderClasses[size]} ${roundedClass} ${statusClasses} flex items-center justify-center transition-all`}
          style={{ backgroundColor: tokenColor }}
          title={token.name}
        >
          <span className="text-white font-bold">
            {displayName}
          </span>
        </div>
      )}
      {showLiquidity && (
        <div className={`text-xs sm:text-sm font-semibold ${isLoser ? 'text-white/30' : 'text-white/80'}`}>
          ${(token.liquidity / 1000).toFixed(2)}K
        </div>
      )}
    </div>
  );
};